import { contactsApi, type Contact } from '../../../api/contacts';
import {
  getEventDetail, probeExternalInviteRoute,
  type ExternalAttendeeDto, type ExternalAttendeeInput, type ExternalAttendeeNewInput, type ExternalInviteEnvelope, type ExternalInviteInfo, type ExternalLastInvite,
} from '../../../api/calendar';
import { registerSessionCache } from '../../../lib/session-caches';
import { tCalendar } from '../../../lib/calendar-i18n';

/**
 * 캘린더 외부 참석자(주소록 연락처) + 자동 초대 메일 — 화면들이 함께 쓰는 순수 도우미 (2026-11-02 · 지시 57).
 *
 * ## 서버가 이 기능을 아나
 *
 * 새 서버는 상세·저장 응답에 `externalInvite` 를 붙이고, 옛 서버는 «아예» 안 붙인다. 응답을 하나라도 받으면
 * 그것으로 정하고(`noteExternalSupport`), 새 일정 창처럼 응답을 받기 전이면 한 번 묻는다(`ensureExternalSupport`).
 * 모르면 «없다»로 다룬다 — 옛 서버에 보내면 칸이 조용히 버려지기 때문이다.
 *
 * ## 주소록
 *
 * 개인·공유를 «따로» 부른다(각 200건). `scope=ALL` 은 쓰지 않는다 — 옛 서버에서 남의 개인 연락처가 섞인다.
 * 탭을 처음 열 때 한 번만 부르고 세션 동안 기억한다(요청 한도). 같은 이메일은 하나로 합친다 — 내 것이 이긴다.
 */

/* ── 지원 여부 ─────────────────────────────────────────────────────────── */

let support: boolean | null = null;
let probing: Promise<boolean> | null = null;

/* ── 주소록 ───────────────────────────────────────────────────────────── */

export const CONTACT_PAGE = 200;

export interface AttendeeContactsData {
  personal: Contact[];
  shared: Contact[];
  personalTotal: number;
  sharedTotal: number;
}

let contactsPromise: Promise<AttendeeContactsData> | null = null;

/** 일정마다 «보낸 적 있는 외부 참석자가 있나» — 삭제 확인 창이 쓴다. */
const sentByEvent = new Map<string, boolean>();

function resetAll(): void {
  support = null;
  probing = null;
  contactsPromise = null;
  sentByEvent.clear();
}
registerSessionCache(resetAll);

/** 시험 전용. */
export function _resetExternalAttendeesForTests(): void {
  resetAll();
}

/**
 * «상세 GET» 응답 하나로 지원 여부를 정한다. `externalInvite` 가 있으면 «안다», 없으면 «모른다(옛 서버)».
 * ⛔ 저장·삭제 응답으로는 정하지 않는다 — 새 서버도 외부 참석자가 없는 일정이면 그 칸을 «안 붙인다».
 */
export function noteExternalSupport(env: ExternalInviteEnvelope): boolean {
  support = env.externalInvite !== undefined;
  return support;
}

export function externalSupportKnown(): boolean | null {
  return support;
}

/** 아직 모르면 한 번 묻는다. 답을 못 얻으면 «없다»로 다룬다(기억하지 않아 다음에 다시 묻는다). */
export function ensureExternalSupport(): Promise<boolean> {
  if (support !== null) return Promise.resolve(support);
  if (probing) return probing;
  let asked: Promise<boolean | null>;
  try {
    asked = probeExternalInviteRoute();
  } catch {
    return Promise.resolve(false);
  }
  probing = asked
    .then((v) => {
      if (v !== null) support = v;
      return v === true;
    })
    .catch(() => false)
    .finally(() => { probing = null; });
  return probing;
}

/** 탭을 처음 열 때 부른다. 실패하면 기억하지 않는다(다음에 다시). */
export function loadAttendeeContacts(): Promise<AttendeeContactsData> {
  if (contactsPromise) return contactsPromise;
  const p = Promise.all([
    contactsApi.list({ scope: 'PERSONAL', limit: CONTACT_PAGE, sort: 'name' }),
    contactsApi.list({ scope: 'SHARED', limit: CONTACT_PAGE, sort: 'name' }),
  ]).then(([mine, shared]) => ({
    personal: Array.isArray(mine?.contacts) ? mine.contacts : [],
    shared: Array.isArray(shared?.contacts) ? shared.contacts : [],
    personalTotal: typeof mine?.total === 'number' ? mine.total : 0,
    sharedTotal: typeof shared?.total === 'number' ? shared.total : 0,
  }));
  contactsPromise = p;
  p.catch(() => { if (contactsPromise === p) contactsPromise = null; });
  return p;
}

/**
 * 고른 외부 참석자 한 사람. 칩·명단에는 이름·회사를 쓴다.
 * ⭐ 49D — 이미 있는 사람은 `attendeeId`(서버의 외부 참석자 id)를 들고 있고 그것으로 보낸다. 새로 고른 사람은
 *   `contactId` 로 보낸다. 연락처가 지워진 사람은 contactId 가 null 이다(그래도 attendeeId 로 남는다).
 */
export interface ExternalPick {
  attendeeId?: string;
  contactId: string | null;
  name: string | null;
  email: string;
  company: string | null;
}

export interface ContactLine {
  contactId: string;
  name: string | null;
  email: string;
  company: string | null;
  department: string | null;
  /** 내 주소록에서 고른 줄인데 공유 주소록에도 같은 이메일이 있다. */
  alsoShared: boolean;
}

const emailKey = (email: string | null | undefined) => (email ?? '').trim().toLowerCase();

function toLine(c: Contact, alsoShared = false): ContactLine {
  return {
    contactId: c.id,
    name: c.name?.trim() || null,
    email: c.email,
    company: c.company?.trim() || null,
    department: c.department?.trim() || null,
    alsoShared,
  };
}

/**
 * 개인·공유 두 묶음 — 같은 이메일은 하나로 합친다. 내 주소록에 있으면 그 줄만 남기고(«공유에도 있음» 표시),
 * 공유 묶음에서는 뺀다. 한 묶음 안의 겹침도 처음 것 하나만 남긴다. 이메일이 빈 연락처는 고를 수 없어 뺀다.
 */
export function mergeContactSections(personal: Contact[], shared: Contact[]): { personal: ContactLine[]; shared: ContactLine[] } {
  const sharedKeys = new Set(shared.map((c) => emailKey(c.email)).filter(Boolean));
  const seen = new Set<string>();
  const outPersonal: ContactLine[] = [];
  for (const c of personal) {
    const k = emailKey(c.email);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    outPersonal.push(toLine(c, sharedKeys.has(k)));
  }
  const outShared: ContactLine[] = [];
  for (const c of shared) {
    const k = emailKey(c.email);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    outShared.push(toLine(c));
  }
  return { personal: outPersonal, shared: outShared };
}

/** 목록 안 검색 — 이름 · 이메일 · 회사 · 부서. */
export function filterContactLines(lines: ContactLine[], query: string): ContactLine[] {
  const q = query.trim().toLowerCase();
  if (!q) return lines;
  return lines.filter((l) => [l.name, l.email, l.company, l.department].some((v) => (v ?? '').toLowerCase().includes(q)));
}

/** 서버의 외부 참석자 → 고른 값. 연락처가 지워진 사람(contactId 없음)도 «그대로» 둔다 — id 로 보낸다(49D). */
export function picksFromAttendees(list: ExternalAttendeeDto[] | undefined): ExternalPick[] {
  return (list ?? []).map((a) => ({
    attendeeId: a.id,
    contactId: typeof a.contactId === 'string' && a.contactId !== '' ? a.contactId : null,
    name: a.name,
    email: a.email,
    company: a.company,
  }));
}

/** 고른 값 하나를 가리키는 열쇠 — 있는 사람은 외부 참석자 id, 새로 고른 사람은 연락처 id. */
export function externalPickKey(p: ExternalPick): string {
  return p.attendeeId ? `a:${p.attendeeId}` : `c:${p.contactId ?? p.email}`;
}

/** 주소록 줄 → 새로 고른 값. 같은 연락처가 원래 명단에 있으면 그 사람(attendeeId 포함)을 되살린다. */
export function pickFromLine(line: ContactLine, original: ReadonlyArray<ExternalPick> = []): ExternalPick {
  const same = original.find((p) => p.contactId === line.contactId);
  if (same) return { ...same };
  return { contactId: line.contactId, name: line.name, email: line.email, company: line.company };
}

/** 이름이 없으면 이메일 — 칩·명단에 쓰는 한 줄 이름. */
export function externalDisplayName(p: { name: string | null; email: string }): string {
  return p.name?.trim() || p.email;
}

const idSet = (list: ExternalPick[]) => [...new Set(list.map(externalPickKey))].sort().join(',');

/** 명단이 바뀌었나 — 사람 열쇠 묶음으로 견준다(순서·겹침 무시). */
export function externalChanged(original: ExternalPick[], now: ExternalPick[]): boolean {
  return idSet(original) !== idSet(now);
}

/**
 * PATCH 에 실을 칸. ⛔ 바꾸지 않았으면 undefined(키를 안 보낸다 — 서버가 그대로 둔다).
 * 원래 명단을 모르면(상세를 못 읽음 · 옛 서버) 절대 보내지 않는다.
 */
export function externalPatchField(original: ExternalPick[] | null, now: ExternalPick[]): ExternalAttendeeInput[] | undefined {
  if (original === null) return undefined;
  if (!externalChanged(original, now)) return undefined;
  // ⭐ 49D — 있는 사람은 늘 { id }, 새로 고른 사람만 { contactId }. 같은 열쇠는 한 번만.
  const seen = new Set<string>();
  const out: ExternalAttendeeInput[] = [];
  for (const p of now) {
    const k = externalPickKey(p);
    if (seen.has(k)) continue;
    seen.add(k);
    if (p.attendeeId) out.push({ id: p.attendeeId });
    else if (p.contactId) out.push({ contactId: p.contactId });
  }
  return out;
}

/** 새 일정(POST)에 실을 칸 — 연락처 id 만(POST 에는 id 를 쓸 수 없다). 없으면 undefined. */
export function externalCreateField(now: ExternalPick[]): ExternalAttendeeNewInput[] | undefined {
  const ids = [...new Set(now.map((p) => p.contactId).filter((v): v is string => typeof v === 'string' && v !== ''))];
  return ids.length > 0 ? ids.map((contactId) => ({ contactId })) : undefined;
}

/** 「메일이 나가지 않는다」의 까닭 — 사람 말. 보낼 수 있으면 null. */
export function externalUnavailableText(info: Pick<ExternalInviteInfo, 'available' | 'reason'> | null | undefined): string | null {
  if (!info || info.available) return null;
  if (info.reason === 'NO_SENDING_ACCOUNT') return tCalendar('calendar.external.unavailable_no_account');
  if (info.reason === 'NOT_ORGANIZER') return tCalendar('calendar.external.unavailable_not_organizer');
  return tCalendar('calendar.external.unavailable_other');
}

/** 저장·삭제·다시 보내기 응답을 사람 말 알림으로 — 무엇이 어떻게 되는지만. 변화가 없으면 빈 목록. */
export function externalResultNotices(env: ExternalInviteEnvelope): Array<{ variant: 'info' | 'error'; text: string }> {
  const info = env.externalInvite;
  if (!info) return [];
  const out: Array<{ variant: 'info' | 'error'; text: string }> = [];
  if (info.error) {
    out.push({ variant: 'error', text: tCalendar('calendar.external.notice_processing_failed') });
    return out;
  }
  const results = Array.isArray(info.results) ? info.results : [];
  // ⭐ 57B — n 은 «사람 수»다(메일 통 수가 아니다). 「이후 모두」처럼 한 사람에게 두 통이 가도 1명 — 외부 참석자 id(없으면 이메일)로 센다.
  const count = (s: string) => new Set(results.filter((r) => r.status === s).map((r) => r.attendeeId ?? r.email.trim().toLowerCase())).size;
  const queued = count('queued');
  const failed = count('failed');
  const noAccount = count('skipped_no_account');
  const daily = count('skipped_daily_limit');
  const notOrganizer = count('skipped_not_organizer');
  if (queued > 0) {
    const cancelOnly = results.filter((r) => r.status === 'queued').every((r) => r.method === 'CANCEL');
    out.push({ variant: 'info', text: tCalendar(cancelOnly ? 'calendar.external.notice_cancel_queued' : 'calendar.external.notice_queued', { n: queued }) });
  }
  if (noAccount > 0) out.push({ variant: 'info', text: tCalendar('calendar.external.unavailable_no_account') });
  if (daily > 0) out.push({ variant: 'error', text: tCalendar('calendar.external.notice_daily_limit', { n: daily }) });
  if (failed > 0) out.push({ variant: 'error', text: tCalendar('calendar.external.notice_failed', { n: failed }) });
  if (notOrganizer > 0) out.push({ variant: 'info', text: tCalendar('calendar.external.notice_not_organizer', { n: notOrganizer }) });
  return out;
}

/**
 * ⭐ 57B (결정) — 「보내는 중」이 이만큼 넘게 멈춰 있으면 «보내지 못했을 수 있음»으로 본다.
 *   서버의 최악 배달 시간(약 5분 — 50명 × 통당 20초 ÷ 동시 3통)의 두 배. 서버가 그 사이 멈추면 queued 로 남고
 *   스스로 다시 보내지 않는다 ⇒ 사람이 「다시 보내기」로 복구한다.
 */
export const QUEUED_STALE_MS = 10 * 60_000;

/** 멈춘 「보내는 중」인가 — 시각을 모르면 판정하지 않는다(멈춤으로 보지 않는다). */
export function isStaleQueued(li: ExternalLastInvite | null, now: number = Date.now()): boolean {
  if (!li || li.status !== 'queued' || !li.at) return false;
  const at = Date.parse(li.at);
  return Number.isFinite(at) && now - at > QUEUED_STALE_MS;
}

/** 상세 명단의 상태 글자. 보낸 적이 없으면 「아직 보내지 않음」. */
export function lastInviteLabel(li: ExternalLastInvite | null, now: number = Date.now()): { text: string; tone: 'muted' | 'ok' | 'warn' | 'bad' } {
  if (!li) return { text: tCalendar('calendar.external.status_none'), tone: 'muted' };
  const cancel = li.method === 'CANCEL';
  if (isStaleQueued(li, now)) return { text: tCalendar('calendar.external.status_stale_queued'), tone: 'warn' };
  switch (li.status) {
    case 'queued': return { text: tCalendar(cancel ? 'calendar.external.status_cancel_queued' : 'calendar.external.status_queued'), tone: 'muted' };
    case 'sent': return { text: tCalendar(cancel ? 'calendar.external.status_cancel_sent' : li.kind === 'update' ? 'calendar.external.status_update_sent' : 'calendar.external.status_sent'), tone: 'ok' };
    case 'failed': return { text: tCalendar('calendar.external.status_failed'), tone: 'bad' };
    case 'skipped_no_account': return { text: tCalendar('calendar.external.status_skipped_no_account'), tone: 'warn' };
    case 'skipped_daily_limit': return { text: tCalendar('calendar.external.status_skipped_daily_limit'), tone: 'warn' };
    case 'skipped_not_organizer': return { text: tCalendar('calendar.external.status_skipped_not_organizer'), tone: 'warn' };
    default: return { text: tCalendar('calendar.external.status_unknown'), tone: 'muted' };
  }
}

/**
 * 다시 보내기를 권할 줄인가 — 못 갔거나(실패·건너뜀), 아직 한 번도 안 갔거나, 「보내는 중」이 오래 멈췄다(57B).
 * ⛔ 이미 받은 사람(보냄)과 아직 정상으로 «보내는 중»인 사람은 아니다 — 그들에게 또 가면 안 된다.
 */
export function needsResend(li: ExternalLastInvite | null, now: number = Date.now()): boolean {
  if (!li) return true;
  if (li.method === 'CANCEL') return false;
  if (li.status === 'queued') return isStaleQueued(li, now);
  return li.status !== 'sent';
}

/**
 * 「다시 보내기」에 실을 사람 id — ⭐ 57B: 늘 사람을 «골라» 보낸다. 서버는 비우면 남은 전원에게 다시 보내므로
 *   빈 목록·빈 칸은 절대 만들지 않는다. 보낼 사람이 없으면 null(부르지 않는다).
 */
export function resendTargets(list: ExternalAttendeeDto[], now: number = Date.now()): string[] | null {
  const ids = [...new Set(list.filter((a) => needsResend(a.lastInvite, now)).map((a) => a.id))];
  return ids.length > 0 ? ids : null;
}

/** 지우면 취소 메일이 나가나 — 초대가 «간 적»(보냄·보내는 중)이 있는 사람이 한 명이라도 있으면. */
export function hasSentExternal(list: ExternalAttendeeDto[] | undefined): boolean {
  return (list ?? []).some((a) => a.lastInvite?.method === 'REQUEST' && (a.lastInvite.status === 'sent' || a.lastInvite.status === 'queued'));
}

export function rememberExternalSent(eventId: string, list: ExternalAttendeeDto[] | undefined): void {
  sentByEvent.set(eventId, hasSentExternal(list));
}

/**
 * 삭제 확인 창에 붙일 한 줄 — 보낸 적 있는 외부 참석자가 있으면 「취소 메일이 발송됩니다」, 아니면 null.
 * 상세에서 이미 읽었으면 그 값을 쓰고, 아니면 한 번 읽는다. 못 읽으면 null(확인 창은 종전 그대로).
 */
export async function externalCancelNotice(eventId: string): Promise<string | null> {
  let sent = sentByEvent.get(eventId);
  if (sent === undefined) {
    if (support === false) return null;
    try {
      const detail = await getEventDetail(eventId);
      noteExternalSupport(detail);
      sent = hasSentExternal(detail.externalAttendees);
      sentByEvent.set(eventId, sent);
    } catch {
      return null;
    }
  }
  return sent ? tCalendar('calendar.external.delete_notice') : null;
}
